import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MessageCircle, Send, RefreshCw, Star, ChevronLeft,
  Inbox, Phone, Mail, ShieldOff, User,
  Crown, AlertTriangle, X, CheckCircle, Loader2,
  XCircle, RotateCcw, TrendingUp, ArrowUpCircle
} from "lucide-react";

const API = window.location.origin;
const PASSCODE = "12345678";

const STATUS_CONFIG = {
  open:      { label: "LIVE",     cls: "bg-amber-500/20 text-amber-400 border-amber-500/40",  dot: "bg-amber-400 animate-pulse" },
  replied:   { label: "REPLIED",  cls: "bg-green-500/20 text-green-400 border-green-500/40",   dot: "bg-green-400" },
  closed:    { label: "CLOSED",   cls: "bg-slate-500/20 text-slate-400 border-slate-500/40",   dot: "bg-slate-500" },
  escalated: { label: "ESCALATED",cls: "bg-rose-500/20 text-rose-400 border-rose-500/40",      dot: "bg-rose-500 animate-pulse" },
};

const RECIPIENT_CONFIG = {
  lokesh_sir:   { label: "Lokesh Sir",   badge: "bg-amber-500/20 text-amber-300 border border-amber-500/40" },
  student_care: { label: "Student Care", badge: "bg-indigo-500/20 text-indigo-300 border border-indigo-500/40" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function convStatus(conv) {
  if (conv.is_escalated) return "escalated";
  return conv.status || "open";
}

// ── Mini Stats Dashboard ─────────────────────────────────────────────────────
function StatsDashboard({ stats, period, onPeriodChange, loading }) {
  const ps = stats?.period_stats || {};
  const at = stats?.all_time || {};

  const cards = [
    { key: "live",      label: "Live Doubts",  val: ps.live ?? 0,      atVal: at.live ?? 0,      color: "amber",  pulse: true },
    { key: "replied",   label: "Replied",       val: ps.replied ?? 0,   atVal: at.replied ?? 0,   color: "green",  pulse: false },
    { key: "escalated", label: "Escalated",     val: ps.escalated ?? 0, atVal: at.escalated ?? 0, color: "rose",   pulse: (ps.escalated ?? 0) > 0 },
    { key: "closed",    label: "Closed",        val: ps.closed ?? 0,    atVal: at.closed ?? 0,    color: "slate",  pulse: false },
  ];

  const colorMap = {
    amber: { bg: "bg-amber-500/10", border: "border-amber-500/25", text: "text-amber-400", sub: "text-amber-500/60" },
    green: { bg: "bg-green-500/10", border: "border-green-500/25", text: "text-green-400", sub: "text-green-500/60" },
    rose:  { bg: "bg-rose-500/10",  border: "border-rose-500/25",  text: "text-rose-400",  sub: "text-rose-500/60" },
    slate: { bg: "bg-slate-500/10", border: "border-slate-500/20", text: "text-slate-300", sub: "text-slate-600" },
  };

  return (
    <div className="border-b border-white/5 bg-[#080d1a] px-5 py-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-indigo-400" />
          <span className="text-sm font-bold text-white">Support Overview</span>
          {loading && <Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin" />}
        </div>
        <div className="flex gap-1 bg-white/5 rounded-xl p-1 border border-white/5">
          {[["daily","Today"],["monthly","Month"],["yearly","Year"]].map(([v, l]) => (
            <button key={v} onClick={() => onPeriodChange(v)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                period === v ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-white"
              }`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {cards.map(c => {
          const cl = colorMap[c.color];
          return (
            <div key={c.key} className={`rounded-xl p-3 border relative overflow-hidden ${cl.bg} ${cl.border}`}>
              {c.pulse && c.val > 0 && (
                <span className={`absolute top-2.5 right-2.5 w-1.5 h-1.5 rounded-full ${colorMap[c.color].text.replace("text-","bg-")} animate-ping`} />
              )}
              <p className={`text-2xl font-black ${cl.text}`}>{c.val}</p>
              <p className="text-[11px] text-white/60 font-medium">{c.label}</p>
              <p className={`text-[10px] mt-0.5 ${cl.sub}`}>All: {c.atVal}</p>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      {at.total > 0 && (
        <div className="flex gap-0.5 rounded-full overflow-hidden h-1.5 mt-3">
          {at.live > 0      && <div className="bg-amber-500 transition-all" style={{ width: `${(at.live / at.total) * 100}%` }} />}
          {at.replied > 0   && <div className="bg-green-500 transition-all" style={{ width: `${(at.replied / at.total) * 100}%` }} />}
          {at.escalated > 0 && <div className="bg-rose-500 transition-all" style={{ width: `${(at.escalated / at.total) * 100}%` }} />}
          {at.closed > 0    && <div className="bg-slate-500 transition-all" style={{ width: `${(at.closed / at.total) * 100}%` }} />}
        </div>
      )}
    </div>
  );
}

// ── Student Profile Panel ─────────────────────────────────────────────────────
function StudentProfilePanel({ conversation, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRefundConfirm, setShowRefundConfirm] = useState(false);
  const [refundReason, setRefundReason] = useState("Student requested refund");
  const [refunding, setRefunding] = useState(false);

  useEffect(() => {
    if (!conversation) return;
    setLoading(true); setProfile(null);
    const email = conversation.student_email;
    const mobile = conversation.student_id?.startsWith("sess_") ? "" : conversation.student_id;
    const q = email ? `email=${encodeURIComponent(email)}` : mobile ? `mobile=${encodeURIComponent(mobile)}` : "";
    if (!q) { setLoading(false); return; }
    axios.get(`${API}/api/support-chat/admin/student-profile?${q}&passcode=${PASSCODE}`)
      .then(({ data }) => setProfile(data))
      .catch(() => setProfile({ found: false }))
      .finally(() => setLoading(false));
  }, [conversation]);

  const handleRefund = async () => {
    setRefunding(true);
    try {
      await axios.post(`${API}/api/support-chat/admin/process-refund`, {
        email: profile.email || "", mobile: profile.mobile || "",
        reason: refundReason, passcode: PASSCODE,
      });
      toast.success("Premium access revoked");
      setProfile(prev => ({ ...prev, is_premium: false }));
      setShowRefundConfirm(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
    finally { setRefunding(false); }
  };

  return (
    <div className="w-64 shrink-0 border-l border-white/5 bg-[#090e1a] flex flex-col overflow-y-auto">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Student Profile</span>
        <button onClick={onClose} className="text-slate-600 hover:text-slate-300"><X className="w-3.5 h-3.5" /></button>
      </div>
      {loading ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-600" /></div>
      ) : !profile?.found ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-600 px-4 text-center">
          <User className="w-7 h-7 opacity-30" />
          <p className="text-xs">No registered account found.</p>
        </div>
      ) : (
        <div className="p-4 space-y-4">
          <div className="flex flex-col items-center gap-2 pb-3 border-b border-white/5">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center text-lg font-bold text-white">
              {profile.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <p className="text-white font-semibold text-sm">{profile.name}</p>
            {profile.is_premium ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] font-bold">
                <Crown className="w-3 h-3" /> Premium
              </span>
            ) : (
              <span className="text-[10px] text-slate-500 border border-slate-700 px-2 py-0.5 rounded-full">Free</span>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-white/[0.03] rounded-xl px-3 py-2">
              <Phone className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <div><p className="text-[10px] text-slate-600">Mobile</p><p className="text-xs text-white font-medium">{profile.mobile || "—"}</p></div>
            </div>
            <div className="flex items-center gap-2 bg-white/[0.03] rounded-xl px-3 py-2">
              <Mail className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <div className="min-w-0"><p className="text-[10px] text-slate-600">Email</p><p className="text-xs text-white font-medium truncate">{profile.email || "—"}</p></div>
            </div>
          </div>
          {(profile.payment_id || profile.order_id) && (
            <div className="bg-white/[0.03] rounded-xl px-3 py-2.5 space-y-1.5">
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Payment</p>
              {profile.payment_id && (
                <div><p className="text-[10px] text-slate-600">Payment ID</p><p className="text-[10px] text-slate-300 font-mono break-all">{profile.payment_id}</p></div>
              )}
              {profile.order_id && (
                <div><p className="text-[10px] text-slate-600">Order ID</p><p className="text-[10px] text-slate-300 font-mono break-all">{profile.order_id}</p></div>
              )}
            </div>
          )}
          {profile.is_premium && (
            !showRefundConfirm ? (
              <button onClick={() => setShowRefundConfirm(true)} data-testid="initiate-refund-btn"
                className="w-full flex items-center justify-center gap-1.5 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 rounded-xl py-2 text-xs font-semibold transition-all">
                <ShieldOff className="w-3.5 h-3.5" /> Revoke Premium
              </button>
            ) : (
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-3 space-y-2">
                <p className="text-[11px] text-rose-400 font-semibold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Confirm Revoke</p>
                <textarea value={refundReason} onChange={e => setRefundReason(e.target.value)} rows={2}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white placeholder:text-slate-600 focus:outline-none resize-none" />
                <div className="flex gap-1.5">
                  <button onClick={() => setShowRefundConfirm(false)} className="flex-1 bg-white/5 hover:bg-white/10 text-slate-400 rounded-lg py-1.5 text-[10px]">Cancel</button>
                  <button onClick={handleRefund} disabled={refunding} data-testid="confirm-refund-btn"
                    className="flex-1 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded-lg py-1.5 text-[10px] font-bold flex items-center justify-center gap-1">
                    {refunding ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />} Confirm
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Admin Chat Page ──────────────────────────────────────────────────────
export default function SupportChatAdmin() {
  const [authed, setAuthed]           = useState(false);
  const [pc, setPc]                   = useState("");
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected]       = useState(null);
  const [messages, setMessages]       = useState([]);
  const [reply, setReply]             = useState("");
  const [adminName, setAdminName]     = useState("Support Team");
  const [loading, setLoading]         = useState(false);
  const [filter, setFilter]           = useState("all");
  const [showProfile, setShowProfile] = useState(false);

  // Escalation state
  const [showEscModal, setShowEscModal] = useState(false);
  const [escNote, setEscNote]           = useState("");
  const [escalating, setEscalating]     = useState(false);

  // Stats dashboard
  const [stats, setStats]       = useState(null);
  const [period, setPeriod]     = useState("daily");
  const [statsLoading, setStatsLoading] = useState(false);

  const msgEndRef = useRef(null);
  const pollRef   = useRef(null);

  const fetchStats = useCallback(async (p) => {
    setStatsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/support-chat/admin/dashboard?passcode=${PASSCODE}&period=${p}`);
      setStats(data);
    } catch {} finally { setStatsLoading(false); }
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/support-chat/admin/conversations?passcode=${PASSCODE}`);
      setConversations(data.conversations || []);
    } catch { toast.error("Failed to load conversations"); }
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const { data } = await axios.get(`${API}/api/support-chat/admin/messages/${convId}?passcode=${PASSCODE}`);
      setMessages(data.messages || []);
      await axios.post(`${API}/api/support-chat/admin/mark-read`, { conversation_id: convId, passcode: PASSCODE });
      setConversations(prev => prev.map(c => c.conversation_id === convId ? { ...c, unread_count: 0 } : c));
    } catch { toast.error("Failed to load messages"); }
  }, []);

  useEffect(() => {
    if (!authed) return;
    fetchConversations(); fetchStats(period);
    pollRef.current = setInterval(fetchConversations, 8000);
    return () => clearInterval(pollRef.current);
  }, [authed, fetchConversations, fetchStats, period]);

  useEffect(() => {
    if (!selected) return;
    fetchMessages(selected.conversation_id);
    const t = setInterval(() => fetchMessages(selected.conversation_id), 5000);
    return () => clearInterval(t);
  }, [selected, fetchMessages]);

  useEffect(() => { msgEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleLogin = () => {
    if (pc === PASSCODE) setAuthed(true);
    else toast.error("Wrong passcode");
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/support-chat/admin/reply`, {
        conversation_id: selected.conversation_id,
        message: reply.trim(), admin_name: adminName, passcode: PASSCODE,
      });
      setReply(""); fetchMessages(selected.conversation_id);
      setConversations(prev => prev.map(c =>
        c.conversation_id === selected.conversation_id
          ? { ...c, last_message: `[Team]: ${reply.trim().slice(0, 60)}`, status: "replied" } : c
      ));
      setSelected(prev => prev ? { ...prev, status: "replied" } : prev);
      fetchStats(period);
    } catch { toast.error("Failed to send reply"); }
    finally { setLoading(false); }
  };

  const closeConversation = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/api/support-chat/admin/close`, { conversation_id: selected.conversation_id, passcode: PASSCODE });
      toast.success("Conversation closed");
      const upd = { ...selected, status: "closed" };
      setConversations(prev => prev.map(c => c.conversation_id === selected.conversation_id ? upd : c));
      setSelected(upd); fetchStats(period);
    } catch { toast.error("Failed to close"); }
  };

  const reopenConversation = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/api/support-chat/admin/reopen`, { conversation_id: selected.conversation_id, passcode: PASSCODE });
      toast.success("Conversation reopened");
      const upd = { ...selected, status: "open" };
      setConversations(prev => prev.map(c => c.conversation_id === selected.conversation_id ? upd : c));
      setSelected(upd); fetchStats(period);
    } catch { toast.error("Failed to reopen"); }
  };

  const escalateConversation = async () => {
    if (!selected) return;
    setEscalating(true);
    try {
      await axios.post(`${API}/api/support-chat/admin/escalate`, {
        conversation_id: selected.conversation_id, note: escNote, passcode: PASSCODE
      });
      toast.success("Escalated to Lokesh Sir");
      const upd = { ...selected, is_escalated: true };
      setConversations(prev => prev.map(c => c.conversation_id === selected.conversation_id ? upd : c));
      setSelected(upd); setShowEscModal(false); setEscNote("");
      fetchMessages(selected.conversation_id); fetchStats(period);
    } catch { toast.error("Escalation failed"); }
    finally { setEscalating(false); }
  };

  const deescalateConversation = async () => {
    if (!selected) return;
    try {
      await axios.post(`${API}/api/support-chat/admin/deescalate`, { conversation_id: selected.conversation_id, passcode: PASSCODE });
      toast.success("Escalation removed");
      const upd = { ...selected, is_escalated: false };
      setConversations(prev => prev.map(c => c.conversation_id === selected.conversation_id ? upd : c));
      setSelected(upd); fetchStats(period);
    } catch { toast.error("Failed"); }
  };

  const FILTERS = [
    ["all", "All"],
    ["escalated", "Escalated"],
    ["lokesh_sir", "Lokesh Sir"],
    ["student_care", "Care"],
  ];

  const filtered = conversations.filter(c => {
    if (filter === "all") return true;
    if (filter === "escalated") return c.is_escalated;
    return c.recipient === filter;
  });

  // ── Passcode Gate ──────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="w-full max-w-sm bg-[#0d1117] border border-white/10 rounded-2xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-indigo-600/20 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">Support Chat</h1>
              <p className="text-slate-500 text-xs">Admin access only</p>
            </div>
          </div>
          <input type="password" placeholder="Enter passcode" value={pc}
            onChange={e => setPc(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm mb-4 focus:outline-none focus:border-indigo-500/60"
            data-testid="chat-admin-passcode" />
          <button onClick={handleLogin} data-testid="chat-admin-login"
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-xl text-sm transition-all">
            Access Chat
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712] text-white flex flex-col" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Header */}
      <div className="border-b border-white/5 bg-[#0d1117]/80 backdrop-blur-sm px-6 py-3.5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-5 h-5 text-indigo-400" />
          <h1 className="font-bold text-white">Student Support Chat</h1>
          <span className="text-xs bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded-full">
            {conversations.length} conversations
          </span>
        </div>
        <div className="flex items-center gap-3">
          <input value={adminName} onChange={e => setAdminName(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white w-36 focus:outline-none focus:border-indigo-500/40"
            placeholder="Your name..." />
          <button onClick={() => { fetchConversations(); fetchStats(period); }} className="text-slate-500 hover:text-white transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats Dashboard at top */}
      <StatsDashboard stats={stats} period={period} loading={statsLoading}
        onPeriodChange={p => { setPeriod(p); fetchStats(p); }} />

      <div className="flex flex-1 overflow-hidden" style={{ height: "calc(100vh - 165px)" }}>
        {/* Sidebar */}
        <div className="w-72 shrink-0 border-r border-white/5 flex flex-col bg-[#0a0f1e]">
          {/* Filter tabs */}
          <div className="flex gap-1 p-2 border-b border-white/5">
            {FILTERS.map(([val, label]) => (
              <button key={val} onClick={() => setFilter(val)} data-testid={`filter-${val}`}
                className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                  filter === val ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-300 hover:bg-white/5"
                }`}>
                {label}
                {val === "escalated" && conversations.filter(c => c.is_escalated).length > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[9px] bg-rose-500 text-white rounded-full font-bold">
                    {conversations.filter(c => c.is_escalated).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-slate-600 gap-2">
                <Inbox className="w-6 h-6" /><p className="text-xs">No conversations</p>
              </div>
            ) : filtered.map(conv => {
              const st = convStatus(conv);
              const sCfg = STATUS_CONFIG[st] || STATUS_CONFIG.open;
              const rc = RECIPIENT_CONFIG[conv.recipient] || RECIPIENT_CONFIG.student_care;
              const isSelected = selected?.conversation_id === conv.conversation_id;
              return (
                <button key={conv.conversation_id}
                  onClick={() => { setSelected(conv); setShowProfile(false); }}
                  data-testid={`conv-${conv.conversation_id}`}
                  className={`w-full text-left px-3 py-3 border-b border-white/[0.04] transition-all ${
                    isSelected ? "bg-indigo-600/10 border-l-2 border-l-indigo-500" : "hover:bg-white/[0.03]"
                  } ${conv.is_escalated ? "border-l-2 border-l-rose-500/50" : ""}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-6 h-6 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shrink-0 text-[11px] font-bold text-white`}>
                        {conv.student_name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <span className="text-xs font-medium text-white truncate">{conv.student_name}</span>
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="shrink-0 bg-indigo-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{conv.unread_count}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-md font-medium border ${rc.badge}`}>{rc.label}</span>
                    {conv.is_escalated && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-md font-bold bg-rose-500/20 text-rose-400 border border-rose-500/40 flex items-center gap-0.5">
                        <ArrowUpCircle className="w-2.5 h-2.5" /> Escalated
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 truncate">{conv.last_message}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-[9px] text-slate-600">{timeAgo(conv.last_updated)}</p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border ${sCfg.cls}`}>{sCfg.label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {!selected ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-600 gap-3">
              <MessageCircle className="w-10 h-10 opacity-30" />
              <p className="text-sm">Select a conversation to view messages</p>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="px-5 py-3 border-b border-white/5 bg-[#0d1117]/60 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-white lg:hidden"><ChevronLeft className="w-5 h-5" /></button>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center font-bold text-sm text-white">
                    {selected.student_name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-white font-semibold text-sm">{selected.student_name}</span>
                      {selected.is_escalated && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 font-bold">
                          <ArrowUpCircle className="w-2.5 h-2.5" /> Escalated to Lokesh Sir
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500">{selected.student_email || "No email"}</p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* Status badge */}
                  {(() => {
                    const st = convStatus(selected);
                    const sCfg = STATUS_CONFIG[st] || STATUS_CONFIG.open;
                    return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${sCfg.cls}`}>{sCfg.label}</span>;
                  })()}

                  {/* Escalate / De-escalate */}
                  {!selected.is_escalated && selected.status !== "closed" ? (
                    <button onClick={() => setShowEscModal(true)} data-testid="escalate-btn"
                      className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border bg-rose-500/10 border-rose-500/30 text-rose-400 hover:bg-rose-500/20 transition-all font-medium">
                      <ArrowUpCircle className="w-3.5 h-3.5" /> Escalate
                    </button>
                  ) : selected.is_escalated ? (
                    <button onClick={deescalateConversation} data-testid="deescalate-btn"
                      className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border bg-white/5 border-white/10 text-slate-400 hover:text-white transition-all">
                      <X className="w-3.5 h-3.5" /> Remove Escalation
                    </button>
                  ) : null}

                  {/* Close / Reopen */}
                  {selected.status === "closed" ? (
                    <button onClick={reopenConversation} data-testid="reopen-conv-btn"
                      className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20 transition-all">
                      <RotateCcw className="w-3.5 h-3.5" /> Reopen
                    </button>
                  ) : (
                    <button onClick={closeConversation} data-testid="close-conv-btn"
                      className="flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border bg-white/5 border-white/10 text-slate-400 hover:text-rose-400 hover:border-rose-500/30 transition-all">
                      <XCircle className="w-3.5 h-3.5" /> Close
                    </button>
                  )}

                  <button onClick={() => setShowProfile(v => !v)} data-testid="view-profile-btn"
                    className={`flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-lg border transition-all ${
                      showProfile ? "bg-indigo-600/20 border-indigo-500/40 text-indigo-300" : "bg-white/[0.04] border-white/10 text-slate-400 hover:text-white"
                    }`}>
                    <User className="w-3.5 h-3.5" /> Profile
                  </button>
                </div>
              </div>

              {/* Escalate modal */}
              {showEscModal && (
                <div className="mx-5 mt-3 bg-rose-500/10 border border-rose-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowUpCircle className="w-4 h-4 text-rose-400" />
                    <p className="text-sm font-bold text-rose-300">Escalate to Lokesh Sir</p>
                  </div>
                  <p className="text-[11px] text-rose-300/70 mb-3">This will flag the conversation for Lokesh Sir's attention. The student will not see this action.</p>
                  <textarea value={escNote} onChange={e => setEscNote(e.target.value)}
                    placeholder="Add a note for Lokesh Sir (optional)..." rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none resize-none mb-3"
                    data-testid="escalate-note-input" />
                  <div className="flex gap-2">
                    <button onClick={() => { setShowEscModal(false); setEscNote(""); }}
                      className="flex-1 bg-white/5 hover:bg-white/10 text-slate-400 rounded-xl py-2 text-sm transition-all">Cancel</button>
                    <button onClick={escalateConversation} disabled={escalating} data-testid="confirm-escalate-btn"
                      className="flex-1 bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded-xl py-2 text-sm font-bold transition-all flex items-center justify-center gap-2">
                      {escalating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpCircle className="w-4 h-4" />}
                      Escalate Now
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-1 overflow-hidden">
                {/* Messages */}
                <div className="flex-1 flex flex-col min-w-0">
                  <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                    {messages.length === 0 ? (
                      <div className="text-center text-slate-600 text-sm py-10">No messages yet</div>
                    ) : messages.map(msg => {
                      const isAdmin  = msg.sender_type === "admin";
                      const isSystem = msg.sender_type === "system";
                      if (isSystem) return (
                        <div key={msg.id} className="flex justify-center">
                          <span className="text-[10px] bg-rose-500/10 border border-rose-500/20 text-rose-300 px-3 py-1 rounded-full">
                            {msg.message}
                          </span>
                        </div>
                      );
                      return (
                        <div key={msg.id} className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[75%] flex flex-col gap-0.5 ${isAdmin ? "items-end" : "items-start"}`}>
                            <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                              isAdmin ? "bg-indigo-600 text-white rounded-br-md" : "bg-white/[0.06] text-slate-200 rounded-bl-md border border-white/[0.08]"
                            }`}>{msg.message}</div>
                            <span className="text-[10px] text-slate-600 px-1">{msg.sender_name} · {timeAgo(msg.created_at)}</span>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={msgEndRef} />
                  </div>

                  {/* Reply box */}
                  <div className="px-5 py-3 border-t border-white/5 bg-[#0d1117]/60">
                    <div className="flex gap-2 items-end">
                      <textarea value={reply} onChange={e => setReply(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                        placeholder="Reply... (Enter to send)" rows={2}
                        data-testid="admin-reply-input"
                        className="flex-1 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/50 resize-none" />
                      <button onClick={sendReply} disabled={loading || !reply.trim()}
                        data-testid="admin-reply-send"
                        className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white p-3 rounded-xl transition-all shrink-0">
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Student profile panel */}
                {showProfile && <StudentProfilePanel conversation={selected} onClose={() => setShowProfile(false)} />}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
